import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import API from '../api/axios';
import './UniversityPoolSME.css';

const UniversityPoolSME = () => {
  const { user } = useContext(AuthContext);
  const { showToast } = useToast();
  
  const [activeTab, setActiveTab] = useState('pools');
  const [poolsWithVariations, setPoolsWithVariations] = useState([]);
  const [selectedPool, setSelectedPool] = useState(null);
  const [poolVariations, setPoolVariations] = useState([]);
  const [selectedSubQuestion, setSelectedSubQuestion] = useState(null);
  const [subQuestionVariations, setSubQuestionVariations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mySelections, setMySelections] = useState([]);
  const [certificationStatus, setCertificationStatus] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const checkCertification = useCallback(async () => {
    try {
      const { data } = await API.get('/faculty-certification/my-certification');
      if (data.success) {
        setCertificationStatus(data.certification);
        if (!data.certification.is_certified) {
          showToast('University certification required to access this feature', 'error');
        }
      }
    } catch (err) {
      showToast('Failed to verify certification status', 'error');
      setLoading(false);
    }
  }, [showToast]);

  const fetchPoolsWithVariations = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/university-pool-sme/pools-with-variations');
      
      if (data.success) {
        setPoolsWithVariations(data.pool_papers || []);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to fetch pools with variations', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    checkCertification();
  }, [checkCertification]);

  useEffect(() => {
    if (certificationStatus?.is_certified) {
      fetchPoolsWithVariations();
    }
  }, [certificationStatus, fetchPoolsWithVariations]);

  const fetchPoolVariations = async (poolId) => {
    try {
      setLoading(true);
      const { data } = await API.get(`/university-pool-sme/pools/${poolId}/variations`);
      
      if (data.success) {
        setPoolVariations(data.variations || []);
        setSelectedPool(poolId);
        setActiveTab('review');
        fetchMySelections(poolId);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to fetch pool variations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubQuestionVariations = async (poolId, subQuestionId) => {
    try {
      setLoading(true);
      const { data } = await API.get(`/university-pool-sme/pools/${poolId}/sub-questions/${subQuestionId}/variations`);
      
      if (data.success) {
        setSubQuestionVariations(data.variations || []);
        setSelectedSubQuestion(data.sub_question);
        setActiveTab('sub-question-review');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to fetch sub-question variations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchMySelections = async (poolId) => {
    try {
      const { data } = await API.get(`/university-pool-sme/pools/${poolId}/my-selections`);
      
      if (data.success) {
        setMySelections(data.by_sub_question || []);
      }
    } catch (err) {
      console.error('Failed to fetch selections:', err);
    }
  };

  const handleSelectVariation = async (variationId, comments = '') => {
    try {
      const { data } = await API.post(`/university-pool-sme/variations/${variationId}/select`, {
        comments
      });

      if (data.success) {
        showToast('Variation selected successfully', 'success');
        // Refresh current view
        if (selectedSubQuestion) {
          fetchSubQuestionVariations(selectedPool, selectedSubQuestion.pool_sub_question_id);
        } else {
          fetchPoolVariations(selectedPool);
        }
        fetchMySelections(selectedPool);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to select variation', 'error');
    }
  };

  const handleUnselectVariation = async (variationId) => {
    try {
      const { data } = await API.post(`/university-pool-sme/variations/${variationId}/unselect`);

      if (data.success) {
        showToast('Variation unselected successfully', 'success');
        // Refresh current view
        if (selectedSubQuestion) {
          fetchSubQuestionVariations(selectedPool, selectedSubQuestion.pool_sub_question_id);
        } else {
          fetchPoolVariations(selectedPool);
        }
        fetchMySelections(selectedPool);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to unselect variation', 'error');
    }
  };

  const handleConfirmAllSelections = async () => {
    try {
      const { data } = await API.post(`/university-pool-sme/pools/${selectedPool}/confirm-selections`);

      if (data.success) {
        showToast('All selections confirmed successfully', 'success');
        setShowConfirmModal(false);
        fetchPoolsWithVariations();
        fetchMySelections(selectedPool);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to confirm selections', 'error');
    }
  };

  const getVariationStatusBadge = (variation) => {
    if (variation.my_review_status === 'selected') {
      return <span className="status-badge selected">Selected</span>;
    }
    return <span className="status-badge pending">Pending Review</span>;
  };

  const groupVariationsBySubQuestion = (variations) => {
    const grouped = {};
    variations.forEach(variation => {
      if (!grouped[variation.full_question_number]) {
        grouped[variation.full_question_number] = [];
      }
      grouped[variation.full_question_number].push(variation);
    });
    return grouped;
  };

  // Show certification requirement if not certified
  if (!certificationStatus?.is_certified) {
    return (
      <div className="university-pool-sme-container">
        <div className="page-header">
          <h1>University Question Pool - SME</h1>
          <p>University certification required to access this feature</p>
        </div>
        
        <div className="certification-required">
          <div className="certification-card">
            <div className="certification-icon">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 10L12 5 2 10l10 5 10-5z" /><path d="M6 12v5c0 1 2.5 2.5 6 2.5s6-1.5 6-2.5v-5" />
              </svg>
            </div>
            <h2>University Certification Required</h2>
            <p>
              To access the University Question Pool system, you need to be certified by your University Admin.
              This certification allows you to review variations from university-wide question pools.
            </p>
            <div className="certification-benefits">
              <h3>Benefits of University Certification:</h3>
              <ul>
                <li>Review variations from all colleges</li>
                <li>Select best variations for university papers</li>
                <li>Contribute to cross-college collaboration</li>
                <li>Enhanced professional recognition</li>
              </ul>
            </div>
            <p className="certification-contact">
              Please contact your University Admin to request certification.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="university-pool-sme-container">
      <div className="page-header">
        <h1>University Question Pool - SME</h1>
        <p>Review and select variations from university-wide question pools</p>
        {user.certification_level === 'university' && (
          <div className="certification-badge">
            <span className="badge university-certified">University Certified</span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs-header">
          <button 
            className={`tab-button ${activeTab === 'pools' ? 'active' : ''}`}
            onClick={() => setActiveTab('pools')}
          >
            Pools with Variations ({poolsWithVariations.length})
          </button>
          {selectedPool && (
            <button 
              className={`tab-button ${activeTab === 'review' ? 'active' : ''}`}
              onClick={() => setActiveTab('review')}
            >
              Review Variations
            </button>
          )}
          {selectedSubQuestion && (
            <button 
              className={`tab-button ${activeTab === 'sub-question-review' ? 'active' : ''}`}
              onClick={() => setActiveTab('sub-question-review')}
            >
              {selectedSubQuestion.full_question_number}
            </button>
          )}
        </div>

        <div className="tab-content">
          {activeTab === 'pools' && (
            <div className="pools-section">
              <div className="section-header">
                <h2>Pools with Variations Ready for Review</h2>
                <p>Select a pool to review and select variations</p>
              </div>

              {poolsWithVariations.length === 0 ? (
                <div className="empty-state">
                  <p>No pools with variations ready for review</p>
                </div>
              ) : (
                <div className="pools-grid">
                  {poolsWithVariations.map(pool => (
                    <div key={pool.pool_paper_id} className="pool-card">
                      <div className="pool-header">
                        <h3>{pool.subject_name} - {pool.paper_title || 'General'}</h3>
                        <span className={`status-badge ${pool.status}`}>
                          {pool.status}
                        </span>
                      </div>
                      
                      <div className="pool-details">
                        <p><strong>Pending Variations:</strong> {pool.pending_variations}</p>
                        <p><strong>Selected Variations:</strong> {pool.selected_variations}</p>
                        <p><strong>Sub-questions:</strong> {pool.sub_questions_with_variations}</p>
                        <p><strong>Total Marks:</strong> {pool.total_marks}</p>
                      </div>

                      <div className="pool-actions">
                        <button 
                          className="btn btn-primary"
                          onClick={() => fetchPoolVariations(pool.pool_paper_id)}
                        >
                          Review Variations
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'review' && selectedPool && (
            <div className="review-section">
              <div className="section-header">
                <h2>Review Variations - Anonymized</h2>
                <p>Select the best variations for each sub-question (4-10 per sub-question)</p>
                <div className="review-actions">
                  <button 
                    className="btn btn-success"
                    onClick={() => setShowConfirmModal(true)}
                    disabled={mySelections.length === 0}
                  >
                    Confirm All Selections
                  </button>
                </div>
              </div>

              {/* Selection Summary */}
              <div className="selection-summary">
                <h3>Selection Summary</h3>
                <div className="summary-grid">
                  {mySelections.map(selection => (
                    <div key={selection.full_question_number} className="summary-item">
                      <span className="question-number">{selection.full_question_number}</span>
                      <span className="selection-count">{selection.selected_count} selected</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Variations by Sub-question */}
              <div className="variations-by-subquestion">
                {Object.entries(groupVariationsBySubQuestion(poolVariations)).map(([questionNumber, variations]) => (
                  <div key={questionNumber} className="subquestion-group">
                    <div className="subquestion-header">
                      <h3>{questionNumber}</h3>
                      <button 
                        className="btn btn-secondary btn-sm"
                        onClick={() => fetchSubQuestionVariations(selectedPool, variations[0].pool_sub_question_id)}
                      >
                        View Details ({variations.length} variations)
                      </button>
                    </div>
                    
                    <div className="variations-preview">
                      {variations.slice(0, 3).map(variation => (
                        <div key={variation.pool_variation_id} className="variation-preview">
                          <div className="variation-header">
                            <span className="variation-number">#{variation.variation_number}</span>
                            <span className="quality-score">Quality: {variation.quality_score}</span>
                            {getVariationStatusBadge(variation)}
                          </div>
                          <div className="variation-text">
                            {variation.question_text.substring(0, 100)}...
                          </div>
                          <div className="variation-actions">
                            {variation.my_review_status === 'selected' ? (
                              <button 
                                className="btn btn-danger btn-sm"
                                onClick={() => handleUnselectVariation(variation.pool_variation_id)}
                              >
                                Unselect
                              </button>
                            ) : (
                              <button 
                                className="btn btn-success btn-sm"
                                onClick={() => handleSelectVariation(variation.pool_variation_id)}
                              >
                                Select
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                      {variations.length > 3 && (
                        <div className="more-variations">
                          +{variations.length - 3} more variations
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'sub-question-review' && selectedSubQuestion && (
            <div className="sub-question-review-section">
              <div className="section-header">
                <h2>{selectedSubQuestion.full_question_number} - Detailed Review</h2>
                <p>Select 4-10 variations for this sub-question</p>
                <div className="selection-info">
                  <span>Selected: {subQuestionVariations.filter(v => v.my_review_status === 'selected').length} / 10</span>
                </div>
              </div>

              <div className="variations-list">
                {subQuestionVariations.map(variation => (
                  <div key={variation.pool_variation_id} className="variation-card">
                    <div className="variation-header">
                      <div className="variation-meta">
                        <span className="variation-number">Variation #{variation.variation_number}</span>
                        <span className="quality-score">Quality: {variation.quality_score}</span>
                        <span className="difficulty-level">Difficulty: {variation.difficulty_level}</span>
                      </div>
                      {getVariationStatusBadge(variation)}
                    </div>
                    
                    <div className="variation-content">
                      <div className="question-text">
                        <h4>Question:</h4>
                        <p>{variation.question_text}</p>
                      </div>
                      
                      {variation.options && (
                        <div className="question-options">
                          <h4>Options:</h4>
                          <ul>
                            {JSON.parse(variation.options).map((option, index) => (
                              <li key={index}>{option}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      <div className="correct-answer">
                        <h4>Answer:</h4>
                        <p>{variation.correct_answer}</p>
                      </div>
                      
                      {variation.reference_source && (
                        <div className="reference-source">
                          <h4>Reference:</h4>
                          <p>{variation.reference_source}</p>
                        </div>
                      )}
                      
                      {variation.difficulty_reason && (
                        <div className="difficulty-reason">
                          <h4>Difficulty Reason:</h4>
                          <p>{variation.difficulty_reason}</p>
                        </div>
                      )}
                    </div>
                    
                    <div className="variation-actions">
                      {variation.my_review_status === 'selected' ? (
                        <button 
                          className="btn btn-danger"
                          onClick={() => handleUnselectVariation(variation.pool_variation_id)}
                        >
                          Unselect Variation
                        </button>
                      ) : (
                        <button 
                          className="btn btn-success"
                          onClick={() => handleSelectVariation(variation.pool_variation_id)}
                          disabled={subQuestionVariations.filter(v => v.my_review_status === 'selected').length >= 10}
                        >
                          Select Variation
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confirm All Selections Modal */}
      {showConfirmModal && (
        <div className="modal-overlay" onClick={() => setShowConfirmModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm All Selections</h3>
              <button 
                className="modal-close"
                onClick={() => setShowConfirmModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <p>Are you sure you want to confirm all your selections? This action cannot be undone.</p>
              
              <div className="selection-summary">
                <h4>Your Selections:</h4>
                {mySelections.map(selection => (
                  <div key={selection.full_question_number} className="summary-row">
                    <span>{selection.full_question_number}:</span>
                    <span>{selection.selected_count} variations selected</span>
                  </div>
                ))}
              </div>
              
              <p className="warning-text">
                After confirmation, the selected variations will be sent to moderators for set generation.
              </p>
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-success"
                onClick={handleConfirmAllSelections}
              >
                Confirm All Selections
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UniversityPoolSME;